import React from 'react';
import type { ReactNode } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  alpha,
  useTheme,
} from '@mui/material';
import { CaptionText, BodyText } from '../typography';
import type { ConnectorParameterTarget } from '../../types';
import ActionHelpTooltip from '../common/ActionHelpTooltip';
import HelpTooltipTrigger from '../common/HelpTooltipTrigger';
import type { HelpValue } from '../common/helpModel';
import {
  SetupConfirmationStrip,
  WorkflowNotice,
  type ReadinessTone,
} from './WorkflowStepComponents';
import { useConfirmationDialog } from '../dialogs/useConfirmationDialog';
import { buildCreateRequiredParametersConsent } from './createRequiredParametersConsent';
import { borderRadius, layoutGap } from '../../../theme/theme';
import ParameterCreationConsentCard from './ParameterCreationConsentCard';
import ParameterSetupModeSelector from './ParameterSetupModeSelector';

export { formatParameterSetupTargetLabel } from './ParameterSetupModeSelector';

interface AppliedStatusProps {
  visible: boolean;
  message: ReactNode;
  onRevert?: () => void;
  severity?: 'success' | 'warning' | 'error' | 'info';
  revertLabel?: string;
  revertHelp?: HelpValue;
}

interface ParameterSetupScopePanelProps {
  title: string;
  description: ReactNode;
  statusLabel: string;
  statusTone: ReadinessTone;
  blocker?: string | null;
  target: ConnectorParameterTarget;
  onTargetChange: (target: ConnectorParameterTarget) => void;
  confirmed: boolean;
  autoConfirmed: boolean;
  onConfirmedChange: (checked: boolean) => void;
  modeLegend: string;
  modeAriaLabel: string;
  action?: ReactNode;
  modeHelp?: ReactNode;
  setupHelp?: HelpValue;
  notice: ReactNode;
  noticeHelp?: ReactNode;
  /** Show noticeHelp as the family-impact notice icon instead of the info icon. */
  noticeHelpAsIcon?: boolean;
  appliedStatus?: AppliedStatusProps;
  children?: ReactNode;
  layout?: 'stacked' | 'sidebar' | 'minimal';
  /** Strip eyebrow text; pass an empty string to hide it. */
  eyebrow?: string;
  disabled?: boolean;
  /** Mode PipeEX recommends for this setup; gets a "PipeEX choice" star marker. */
  recommendedTarget?: ConnectorParameterTarget;
}

const ParameterSetupScopePanel: React.FC<ParameterSetupScopePanelProps> = ({
  title,
  description,
  statusLabel,
  statusTone,
  blocker,
  target,
  onTargetChange,
  confirmed,
  autoConfirmed,
  onConfirmedChange,
  modeLegend,
  modeAriaLabel,
  action,
  modeHelp,
  setupHelp,
  notice,
  noticeHelp,
  noticeHelpAsIcon = false,
  appliedStatus,
  children,
  layout = 'stacked',
  eyebrow,
  disabled = false,
  recommendedTarget,
}) => {
  const theme = useTheme();
  const minimal = layout === 'minimal';
  // The minimal strip has no notice band, so the applied-setup line carries the
  // severity itself. Without this a quarantined record would still read as
  // green success text, which is the opposite of what happened.
  const appliedStatusColor = theme.palette[appliedStatus?.severity ?? 'success'].light;

  const { requestConfirmation, confirmationDialog: createParametersConsentDialog } =
    useConfirmationDialog();

  // Checking "Create required parameters" writes parameters into the active
  // Revit model on Continue, so enabling always goes through a warning first;
  // unchecking passes through immediately.
  const handleConfirmedChange = (checked: boolean) => {
    if (!checked) {
      onConfirmedChange(false);
      return;
    }

    void requestConfirmation(buildCreateRequiredParametersConsent(target)).then((accepted) => {
      if (accepted) {
        onConfirmedChange(true);
      }
    });
  };

  const modeControl = (
    <ParameterSetupModeSelector
      target={target}
      onTargetChange={onTargetChange}
      legend={modeLegend}
      ariaLabel={modeAriaLabel}
      help={modeHelp}
      impact={notice}
      impactHelp={noticeHelp}
      impactHelpAsNoticeIcon={noticeHelpAsIcon}
      minimal={minimal}
      disabled={disabled}
      recommendedTarget={recommendedTarget}
    />
  );

  const confirmControl = (
    <ParameterCreationConsentCard
      checked={Boolean(confirmed)}
      autoConfirmed={Boolean(autoConfirmed)}
      onChange={handleConfirmedChange}
      disabled={disabled}
    />
  );

  if (minimal) {
    return (
      <>
        {createParametersConsentDialog}
      <SetupConfirmationStrip
        title={title}
        description={description}
        statusLabel={statusLabel}
        statusTone={statusTone}
        blocker={blocker}
        eyebrow={eyebrow}
        action={
          setupHelp || action ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.dense, flexWrap: 'wrap' }}>
              {setupHelp ? (
                <HelpTooltipTrigger
                  label={`${title} help`}
                  help={setupHelp}
                  placement="bottom-start"
                />
              ) : null}
              {action}
            </Box>
          ) : undefined
        }
        control={
          <Box sx={{ display: 'grid', gap: layoutGap.dense, minWidth: 0 }}>
            <Box sx={{ display: 'grid', gap: layoutGap.row, minWidth: 0 }}>
              <Box sx={{ minWidth: 0 }}>{modeControl}</Box>
              {confirmControl}
            </Box>
            {children ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.row, flexWrap: 'wrap' }}>
                {children}
              </Box>
            ) : null}
            {appliedStatus?.visible ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.row, flexWrap: 'wrap' }}>
                <CaptionText
                  data-testid="applied-setup-status"
                  data-severity={appliedStatus.severity ?? 'success'}
                  sx={{ color: appliedStatusColor, fontWeight: 700 }}
                >
                  {appliedStatus.message}
                </CaptionText>
                {appliedStatus.onRevert ? (
                  <ActionHelpTooltip help={appliedStatus.revertHelp}>
                    <Button
                      size="small"
                      variant="text"
                      color="warning"
                      onClick={appliedStatus.onRevert}
                      sx={{ minHeight: 26, px: 0.75 }}
                    >
                      {appliedStatus.revertLabel ?? 'Revert parameter setup'}
                    </Button>
                  </ActionHelpTooltip>
                ) : null}
              </Box>
            ) : null}
          </Box>
        }
      />
      </>
    );
  }

  return (
    <>
      {createParametersConsentDialog}
      <SetupConfirmationStrip
        title={title}
        description={description}
        statusLabel={statusLabel}
        statusTone={statusTone}
        blocker={blocker}
        layout={layout}
        eyebrow={eyebrow}
        action={
          setupHelp || action ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.dense, flexWrap: 'wrap' }}>
              {setupHelp ? (
                <HelpTooltipTrigger
                  label={`${title} help`}
                  help={setupHelp}
                  placement="bottom-start"
                />
              ) : null}
              {action}
            </Box>
          ) : undefined
        }
        control={
          <Box
            sx={{
              display: 'grid',
              gap: layoutGap.row,
            }}
          >
            {modeControl}
            {confirmControl}
          </Box>
        }
      />

      {children}

      {appliedStatus?.visible ? (
        <WorkflowNotice severity={appliedStatus.severity ?? 'success'}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: layoutGap.section, flexWrap: 'wrap' }}>
            <BodyText component="span" sx={{ color: 'inherit' }}>
              {appliedStatus.message}
            </BodyText>
            {appliedStatus.onRevert ? (
              <ActionHelpTooltip help={appliedStatus.revertHelp}>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  onClick={appliedStatus.onRevert}
                  sx={{ minHeight: 28 }}
                >
                  {appliedStatus.revertLabel ?? 'Revert parameter setup'}
                </Button>
              </ActionHelpTooltip>
            ) : null}
          </Box>
        </WorkflowNotice>
      ) : null}
    </>
  );
};

export function ProjectCategoryToggleStrip({
  categories,
  selectedCategories,
  onCategoryChange,
  title = 'Project categories',
  help,
  helpLabel,
}: {
  categories: readonly string[];
  selectedCategories: readonly string[];
  onCategoryChange: (category: string, checked: boolean) => void;
  title?: string;
  help?: HelpValue;
  helpLabel?: string;
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: layoutGap.row,
        flexWrap: 'wrap',
        minWidth: 0,
      }}
    >
      <FormControl component="fieldset" size="small" sx={{ minWidth: 0 }}>
        <FormLabel component="legend" sx={{ float: 'left', mr: 0.6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.dense }}>
            <CaptionText sx={{ fontWeight: 700, color: 'inherit', lineHeight: 1.25 }}>
              {title}
            </CaptionText>
            <HelpTooltipTrigger
              label={helpLabel ?? title}
              help={help}
              placement="bottom-start"
            />
          </Box>
        </FormLabel>
        <FormGroup
          row
          sx={{
            gap: layoutGap.dense,
            mt: 0,
            '& .MuiFormControlLabel-root': {
              m: 0,
              minHeight: 26,
              px: 0.45,
              borderRadius: `${borderRadius.sm}px`,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              backgroundColor: alpha(theme.palette.background.default, 0.08),
            },
            '& .MuiCheckbox-root': {
              p: 0.35,
            },
            '& .MuiSvgIcon-root': {
              fontSize: 16,
            },
          }}
        >
          {categories.map((category) => {
            const checked = selectedCategories.includes(category);
            const isOnlySelected = checked && selectedCategories.length === 1;

            return (
              <FormControlLabel
                key={category}
                control={
                  <Checkbox
                    checked={checked}
                    disabled={isOnlySelected}
                    onChange={(event) =>
                      onCategoryChange(category, event.target.checked)
                    }
                    size="small"
                  />
                }
                label={<CaptionText>{category}</CaptionText>}
              />
            );
          })}
        </FormGroup>
      </FormControl>
    </Box>
  );
}

export default ParameterSetupScopePanel;
